/**
 * KeyboardChanter Module
 *
 * Provides keyboard-based note input for the chanter, allowing testing and demos
 * without requiring a physical chanter and microphone.
 */

// Note frequencies (same as standard chanter)
const STANDARD_FREQUENCIES = {
  'Low G': 426,
  'Low A': 480,
  B: 540,
  C: 600,
  D: 640,
  E: 720,
  F: 800,
  'High G': 840,
  'High A': 960,
};

// Mapping of keys to notes
const KEY_MAPPING = {
  g: 'Low G',
  a: 'Low A',
  b: 'B',
  c: 'C',
  d: 'D',
  e: 'E',
  f: 'F',
  G: 'High G',
  A: 'High A',
};

// Export for other components to use
export { KEY_MAPPING, STANDARD_FREQUENCIES };

// Determine if we're in development mode for verbose logging
const IS_DEV = process.env.NODE_ENV === 'development';

class KeyboardChanter {
  constructor() {
    this.currentNote = null;
    this.currentFrequency = null;
    this.isActive = false;
    this.listeners = [];
    this.keydownHandler = this.handleKeyDown.bind(this);
    this.keyupHandler = this.handleKeyUp.bind(this);
    this.lastKeyTime = 0; // Track last key event time
    this.lastLoggedNote = null; // Track last logged note to reduce duplicate logs
    this.keysPressed = {}; // Track all currently pressed keys
    
    // Direct callback for components to implement fast note detection
    this.directNoteCallback = null;
    
    // Debug status
    this.debug = {
      processedEvents: 0,
      ignoredEvents: 0,
      keydownCounts: {},
      keyupCounts: {},
    };
  }

  /**
   * Register a direct callback for immediate note detection
   * This bypasses React's state update system for faster response
   * @param {Function} callback - Function that receives note data immediately on key press/release
   */
  registerDirectCallback(callback) {
    this.directNoteCallback = callback;
    console.log('Direct keyboard callback registered');
    return () => {
      this.directNoteCallback = null;
      console.log('Direct keyboard callback unregistered');
    };
  }

  /**
   * Start listening for keyboard input
   */
  start() {
    if (this.isActive) {
      // Already active, no need to log
      return true;
    }

    // Clear state on start
    this.currentNote = null;
    this.currentFrequency = null;
    this.lastLoggedNote = null;
    this.keysPressed = {};
    
    // Reset debug counters
    this.debug = {
      processedEvents: 0,
      ignoredEvents: 0,
      keydownCounts: {},
      keyupCounts: {},
    };

    // Use capture phase to get events before other handlers
    document.addEventListener('keydown', this.keydownHandler, true);
    document.addEventListener('keyup', this.keyupHandler, true);
    this.isActive = true;

    console.log('Keyboard Chanter activated');
    return true;
  }

  /**
   * Stop listening for keyboard input
   */
  stop() {
    if (!this.isActive) {
      // Already inactive, no need to log
      return;
    }

    document.removeEventListener('keydown', this.keydownHandler, true);
    document.removeEventListener('keyup', this.keyupHandler, true);
    this.isActive = false;
    this.currentNote = null;
    this.currentFrequency = null;
    this.lastLoggedNote = null;
    this.keysPressed = {};
    this.directNoteCallback = null; // Clear the direct callback

    // Notify all listeners that we've stopped
    this.notifyListeners();

    console.log('Keyboard Chanter deactivated');
    
    // Log debug stats
    if (IS_DEV) {
      console.log('Keyboard Chanter stats:', this.debug);
    }
  }

  /**
   * Handle keydown events
   */
  handleKeyDown(event) {
    const key = event.key;
    
    // Debug counter
    this.debug.processedEvents++;
    this.debug.keydownCounts[key] = (this.debug.keydownCounts[key] || 0) + 1;
    
    // Ignore if modifier keys are pressed or if not active
    if (!this.isActive || event.ctrlKey || event.altKey || event.metaKey) {
      this.debug.ignoredEvents++;
      return;
    }

    // Ignore if input element is focused
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      this.debug.ignoredEvents++;
      return;
    }

    // Check if this is a mapped key
    if (KEY_MAPPING[key]) {
      // Track this key as pressed
      this.keysPressed[key] = true;
      
      // Prevent default actions only for mapped keys to avoid interfering with browser shortcuts
      event.preventDefault();
      event.stopPropagation();

      const noteName = KEY_MAPPING[key];
      
      // If we're already playing this note, don't update
      if (this.currentNote === noteName) return;
      
      this.currentNote = noteName;
      this.currentFrequency = STANDARD_FREQUENCIES[noteName];
      this.lastKeyTime = Date.now();

      // More verbose logging in development mode
      if (IS_DEV) {
        console.log(`Keyboard Chanter: Key '${key}' pressed -> Note: ${noteName}`);
      } else if (this.lastLoggedNote !== noteName) {
        // Reduce logging in production - only log when the note changes
        console.log('Keyboard Chanter detected note:', noteName);
      }
      
      this.lastLoggedNote = noteName;
      
      // Call the direct callback if it exists - this bypasses React state updates
      if (this.directNoteCallback) {
        this.directNoteCallback({
          noteName: this.currentNote,
          frequency: this.currentFrequency,
          clarity: 0.9,
          action: 'keydown'
        });
      }

      // Notify regular listeners
      this.notifyListeners();
    }
  }

  /**
   * Handle keyup events
   */
  handleKeyUp(event) {
    const key = event.key;
    
    // Debug counter
    this.debug.processedEvents++;
    this.debug.keyupCounts[key] = (this.debug.keyupCounts[key] || 0) + 1;
    
    // Remove from pressed keys
    if (this.keysPressed[key]) {
      delete this.keysPressed[key];
    }
    
    // Ignore if not active
    if (!this.isActive) {
      this.debug.ignoredEvents++;
      return;
    }
    
    // If this was our current note, clear it
    if (this.currentNote && KEY_MAPPING[key] === this.currentNote) {
      // More verbose logging in development mode
      if (IS_DEV) {
        console.log(`Keyboard Chanter: Key '${key}' released -> Note: ${this.currentNote}`);
      } else if (this.lastLoggedNote === this.currentNote) {
        // Reduce logging in production - only log when significant changes happen
        console.log('Keyboard Chanter releasing note:', this.currentNote);
      }
      
      const releasedNote = this.currentNote;
      const releasedFreq = this.currentFrequency;
      
      this.currentNote = null;
      this.currentFrequency = null;
      this.lastLoggedNote = null;
      
      // Call the direct callback if it exists for keyup
      if (this.directNoteCallback) {
        this.directNoteCallback({
          noteName: null,
          frequency: null,
          clarity: 0.0,
          releasedNote: releasedNote,
          releasedFreq: releasedFreq,
          action: 'keyup'
        });
      }

      // Notify listeners
      this.notifyListeners();
    }
  }

  /**
   * Subscribe to note changes
   * @param {Function} callback - Function to call when note changes
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of current state
   */
  notifyListeners() {
    const noteData = {
      noteName: this.currentNote,
      frequency: this.currentFrequency,
      clarity: this.currentNote ? 0.9 : 0.0, // Simulate high clarity when key is pressed
    };

    for (const listener of this.listeners) {
      listener(noteData);
    }
  }

  /**
   * Force a specific note to be active (for testing)
   * @param {string} noteName - The name of the note to activate
   */
  forceNote(noteName) {
    if (!noteName || !Object.values(KEY_MAPPING).includes(noteName)) {
      this.currentNote = null;
      this.currentFrequency = null;
    } else {
      this.currentNote = noteName;
      this.currentFrequency = STANDARD_FREQUENCIES[noteName];
    }
    
    this.lastKeyTime = Date.now();
    
    // Call the direct callback if it exists
    if (this.directNoteCallback) {
      this.directNoteCallback({
        noteName: this.currentNote,
        frequency: this.currentFrequency,
        clarity: this.currentNote ? 0.9 : 0.0,
        action: 'force'
      });
    }
    
    this.notifyListeners();
    
    return this.currentNote;
  }

  /**
   * Get current note information
   * @returns {Object} Current note data
   */
  getCurrentNote() {
    // If it's been more than 2 seconds since the last key event,
    // do a sanity check to make sure we're not stuck with a key down
    const currentTime = Date.now();
    if (this.currentNote && (currentTime - this.lastKeyTime > 2000)) {
      // Check if any keys are still recorded as pressed
      if (Object.keys(this.keysPressed).length === 0) {
        console.log('Keyboard Chanter detected potentially stuck key:', this.currentNote);
        this.currentNote = null;
        this.currentFrequency = null;
        this.lastLoggedNote = null;
      } else {
        // Update the last key time to avoid constant checks
        this.lastKeyTime = currentTime;
      }
    }

    return {
      noteName: this.currentNote,
      frequency: this.currentFrequency,
      clarity: this.currentNote ? 0.9 : 0.0,
    };
  }

  /**
   * Check if keyboard chanter is active
   * @returns {boolean}
   */
  isKeyboardActive() {
    return this.isActive;
  }
}

// Create and export a singleton instance
const keyboardChanter = new KeyboardChanter();
export default keyboardChanter;
